import React, { useMemo, useState, useEffect, useRef } from 'react';
import { friendlyMessage } from "@/lib/errors";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import DataTable, { Column } from '@/components/reusable/DataTable';
import { shopifyApi } from '@/lib/api/services/shopify';
import { contactsApi, BackendContact } from '@/lib/api/services/contacts';
import { toast } from '@/hooks/use-toast';
import { Search, X, Loader2, RefreshCw } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '@/components/AuthProvider';

interface ShopifyCustomersProps {
    selectedAccountId: string | null;
    isConnected: boolean;
    refreshKey?: number;
}

export function ShopifyCustomers({ selectedAccountId, isConnected, refreshKey = 0 }: ShopifyCustomersProps) {
    const { user } = useAuth();
    const organizationId = user?.organization_id || null;
    const [searchParams, setSearchParams] = useSearchParams();
    const [customers, setCustomers] = useState<BackendContact[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [totalCount, setTotalCount] = useState(0);

    // URL State helpers
    const getParam = (key: string, def: string) => searchParams.get(key) || def;
    const setParam = (key: string, value: string | null) => {
        setSearchParams(prev => {
            const newParams = new URLSearchParams(prev);
            if (value === null) newParams.delete(key);
            else newParams.set(key, value);
            return newParams;
        });
    };

    // State from URL
    const customersSearch = getParam('customers_search', '');
    const [localSearch, setLocalSearch] = useState(customersSearch);
    const debounceRef = useRef<ReturnType<typeof setTimeout>>();

    // Sync local state when URL changes externally
    useEffect(() => { setLocalSearch(customersSearch); }, [customersSearch]);

    // Debounce search — only update URL param after 400ms of no typing
    const handleSearchChange = (value: string) => {
        setLocalSearch(value);
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            setParam('customers_search', value);
        }, 400);
    };
    const customersPage = parseInt(getParam('customers_page', '1'));
    const customersPageSizeStr = getParam('customers_page_size', '10');
    const customersPageSize = parseInt(customersPageSizeStr, 10);

    // Load customers
    const loadCustomers = async () => {
        const targetId = selectedAccountId || organizationId;
        if (!targetId) return;
        setLoading(true);
        try {
            // Fetch contacts with 'Shopify' tag
            // We use the contacts API to get consistent contact data
            const res = await contactsApi.getAll({
                business_account_id: targetId,
                search: customersSearch,
                source: 'shopify', // Filter by Shopify source
                limit: customersPageSize,
                offset: (customersPage - 1) * customersPageSize,
                sort_by: 'created_at',
                order: 'desc'
            });

            if (res.success && Array.isArray(res.data)) {
                setCustomers(res.data);
                setTotalCount(res.filteredTotalCount ?? res.totalCount ?? res.count ?? 0);
            } else {
                setCustomers([]);
                setTotalCount(0);
            }
        } catch (e: any) {
            setError(friendlyMessage(e, 'Failed to load customers'));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (isConnected && (selectedAccountId || organizationId)) {
            loadCustomers();
        }
    }, [isConnected, selectedAccountId, organizationId, customersPage, customersPageSize, customersSearch, refreshKey]);

    const customersColumns: Column<BackendContact>[] = useMemo(() => [
        {
            key: 'name',
            header: 'Name',
            render: (_, contact) => (
                <div className="font-medium text-sm">
                    {contact.name || '—'}
                </div>
            ),
            className: 'font-medium text-sm',
        },
        {
            key: 'email',
            header: 'Email',
            render: (_, contact) => (
                <div className="text-sm text-muted-foreground">
                    {contact.email || '—'}
                </div>
            ),
            className: 'text-sm',
        },
        {
            key: 'phone',
            header: 'Phone',
            render: (_, contact) => (
                <div className="text-sm text-muted-foreground">
                    {contact.phoneNumber || '—'}
                </div>
            ),
            className: 'text-sm',
        },
        {
            key: 'shopify_id',
            header: 'Shopify ID',
            render: (_, contact) => {
                const shopifyId = contact.customFields?.shopify_customer_id || contact.customFields?.shopifyId;
                return (
                    <span className="text-xs text-muted-foreground font-mono">
                        {shopifyId ? `#${String(shopifyId).slice(-8)}` : '—'}
                    </span>
                );
            },
            className: 'text-xs',
        },
        {
            key: 'created_at',
            header: 'Synced At',
            render: (_, contact) => {
                const syncedAt = contact.customFields?.shopify_last_synced_at;
                return (
                    <span className="text-xs text-muted-foreground">
                        {syncedAt ? new Date(syncedAt).toLocaleDateString() : new Date(contact.createdAt).toLocaleDateString()}
                    </span>
                );
            },
            className: 'text-xs text-muted-foreground hidden md:table-cell',
            headerClassName: 'hidden md:table-cell',
        }
    ], []);

    if (!isConnected) {
        return (
            <div className="text-center py-6 sm:py-8 space-y-3 sm:space-y-4">
                <p className="text-xs sm:text-sm text-muted-foreground">Please connect your Shopify shop first to view customers.</p>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            {error && <div className="text-xs sm:text-sm text-destructive p-3 bg-destructive/10 rounded">{error}</div>}

            <div className="flex flex-col sm:flex-row flex-wrap gap-3 items-center justify-between">
                <div className="flex flex-1 gap-2 items-center w-full sm:w-auto">
                    <div className="relative flex-1 sm:max-w-[300px]">
                        <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                        <Input
                            placeholder="Search customers..."
                            value={localSearch}
                            onChange={(e) => handleSearchChange(e.target.value)}
                            className="pl-9 h-9"
                        />
                        {localSearch && (
                            <X className="absolute right-2.5 top-2.5 h-4 w-4 text-muted-foreground cursor-pointer" onClick={() => { setLocalSearch(''); setParam('customers_search', ''); }} />
                        )}
                    </div>
                </div>
            </div>

            <div className="rounded-md border">
                <DataTable
                    columns={customersColumns}
                    data={customers}
                    currentPage={customersPage}
                    pageSize={customersPageSize}
                    onPageChange={(p) => setParam('customers_page', p.toString())}
                    onPageSizeChange={(s) => {
                        setSearchParams(prev => {
                            const newParams = new URLSearchParams(prev);
                            newParams.set('customers_page_size', s.toString());
                            newParams.set('customers_page', '1');
                            return newParams;
                        });
                    }}
                    loading={loading}
                    emptyMessage="No Shopify customers found."
                    showPagination={true}
                    totalItems={totalCount}
                />
            </div>
        </div>
    );
}
